import type { FastifyRequest, FastifyReply } from 'fastify'
import type { Firestore } from 'firebase-admin/firestore'
import type { RedisClient } from '../../core/redis/client.js'
import type { PlannerPreferences, Plan, Stage } from '@my-roadtrip/types'
import { AIService } from './ai.service.js'
import { AIRepository } from './ai.repository.js'
import { POIDatabaseService } from './poi-database.service.js'
import { EnrichmentService } from './enrichment.service.js'
import { PlannerRepository } from '../planner/planner.repository.js'
import { GamificationService } from '../gamification/gamification.service.js'
import { OpenAIService } from '../../services/openai.service.js'
import { SmartTimingService } from '../../services/smartTiming.service.js'
import { ApiResponse } from '../../utils/response'
import { createHash } from 'node:crypto'
import { AI_QUOTA_LIMITS, resolveUserPlanTier as resolveUserPlan } from './ai.quota.js'
import { appEvents, AppEvent } from '../../core/events/eventEmitter.js'
import type { SuggestPOIsRequest } from './ai.schemas.js'
import { createLogger } from '../../common/utils/logger.js'
import { processPoiSuggestion } from './poi.processor.js'
import { geohash } from '@my-roadtrip/utils'
import { extractCoordinates, toDateSafe } from '../../common/utils/normalize.js'
import { isString, isNonEmptyString } from '../../common/utils/type-guards.js'


const logger = createLogger('AIController')

export class AIController {
  private gamificationService!: GamificationService // Lazy-loaded
  private readonly plannerRepository?: PlannerRepository
  private readonly enableAIWorker = process.env.ENABLE_AI_WORKER !== 'false'
  private readonly firestore?: Firestore
  private readonly openaiService: OpenAIService = new OpenAIService()
  private readonly enrichmentService?: EnrichmentService
  private readonly smartTimingService: SmartTimingService = new SmartTimingService()

  constructor(
    private readonly service: AIService = new AIService(),
    private readonly repository: AIRepository = new AIRepository(),
    firestore?: Firestore,
    redis?: RedisClient
  ) {
    this.firestore = firestore
    // Initialize services if dependencies provided
    if (firestore) {
      this.enrichmentService = new EnrichmentService(firestore)
    }
    if (firestore && redis) {
      this.gamificationService = new GamificationService(firestore, redis)
      this.plannerRepository = new PlannerRepository(firestore)
    }
  }

  /**
   * Get GamificationService (lazy-load if not initialized)
   */
  private async getGamificationService(): Promise<GamificationService> {
    if (!this.gamificationService) {
      const { db } = await import('../../core/database/firebase.js')
      const { redis: redisClient } = await import('../../core/database/redis.js')
      this.gamificationService = new GamificationService(db, redisClient)
    }
    return this.gamificationService
  }

  /**
   * Generate cache key for request
   * Uses geographic hashing for better cache hit rates (±600m)
   */
  private getCacheKey(operation: string, params: Record<string, unknown>): string {
    // For POI suggestions, use geographic cache key
    if (operation === 'suggest-pois' && params.location && typeof params.location === 'object') {
      const loc = params.location as { lat: number; lng: number }
      const geoHash = geohash(loc.lat, loc.lng, 6) // ±600m precision

      // Bucket radius for better cache hits (25, 50, 75, 100, 125, 150, 175, 200km)
      const radiusBucket = params.radiusKm
        ? Math.ceil(Number(params.radiusKm) / 25) * 25
        : 50

      // Sort categories for consistent key
      const categories = Array.isArray(params.categories)
        ? [...params.categories].sort().join('|')
        : 'default'

      return `ai:poi:geo:${geoHash}:r${radiusBucket}:${categories}`
    }

    // Fallback to hash-based key for other operations
    const hash = createHash('sha256')
      .update(this.stableStringify(params))
      .digest('hex')
      .substring(0, 16)
    return `ai:${operation}:${hash}`
  }

  private stableStringify(value: unknown): string {
    if (Array.isArray(value)) {
      return `[${value.map(item => this.stableStringify(item)).join(',')}]`
    }

    if (value && typeof value === 'object') {
      const entries = Object.entries(value as Record<string, unknown>)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, v]) => `${key}:${this.stableStringify(v)}`)
      return `{${entries.join('|')}}`
    }

    if (isString(value)) {
      return value.trim().toLowerCase()
    }

    return String(value)
  }

  private buildSuggestionCacheParams(
    userId: string | undefined,
    location: { lat: number; lng: number },
    categories: string[],
    radiusKm: number,
    preferences?: any,
    context?: any,
    metadata?: {
      planId?: string
      stageId?: string
      planName?: string
      stageName?: string
    }
  ) {
    const normalizeId = (value?: string) => {
      if (!isString(value)) return undefined
      const trimmed = value.trim()
      return trimmed.length ? trimmed : undefined
    }

    const normalizeLabel = (value?: string) => {
      if (!isString(value)) return undefined
      const trimmed = value.trim().toLowerCase()
      return trimmed.length ? trimmed.slice(0, 80) : undefined
    }

    const sanitizePoint = (point?: { lat?: number; lng?: number; name?: string }) => {
      if (!point) return undefined

      const safeLat = typeof point.lat === 'number' ? Number(point.lat.toFixed(4)) : undefined
      const safeLng = typeof point.lng === 'number' ? Number(point.lng.toFixed(4)) : undefined
      const safeName = typeof point.name === 'string' ? point.name.trim().slice(0, 60).toLowerCase() : undefined

      if (safeLat === undefined && safeLng === undefined && !safeName) {
        return undefined
      }

      return {
        ...(safeLat !== undefined ? { lat: safeLat } : {}),
        ...(safeLng !== undefined ? { lng: safeLng } : {}),
        ...(safeName ? { name: safeName } : {})
      }
    }

    const planContext = (() => {
      const id = normalizeId(metadata?.planId)
      const name = normalizeLabel(metadata?.planName)
      if (!id && !name) return undefined
      return {
        ...(id ? { id } : {}),
        ...(name ? { name } : {})
      }
    })()

    const stageContext = (() => {
      const id = normalizeId(metadata?.stageId)
      const name = normalizeLabel(metadata?.stageName)
      if (!id && !name) return undefined
      return {
        ...(id ? { id } : {}),
        ...(name ? { name } : {})
      }
    })()

    return {
      user: userId ?? 'anon',
      plan: planContext,
      stage: stageContext,
      location: {
        lat: Number(location.lat.toFixed(4)),
        lng: Number(location.lng.toFixed(4))
      },
      categories: [...categories].sort(),
      radiusKm: Math.round(radiusKm * 10) / 10,
      preferences: preferences
        ? {
          prompt: typeof preferences.prompt === 'string' ? preferences.prompt.trim().toLowerCase() : undefined,
          preferScenic: Boolean(preferences.preferScenic),
          avoidHighways: Boolean(preferences.avoidHighways),
          language:
            typeof preferences.language === 'string'
              ? preferences.language.trim().toLowerCase()
              : undefined
        }
        : undefined,
      context: context
        ? {
          fromPoint: sanitizePoint(context.fromPoint),
          toPoint: sanitizePoint(context.toPoint),
          detourMaxKm:
            typeof context.detourMaxKm === 'number'
              ? Math.round(context.detourMaxKm * 10) / 10
              : undefined,
          timeAvailable:
            typeof context.timeAvailable === 'number'
              ? Math.round(context.timeAvailable)
              : undefined
        }
        : undefined
    }
  }

  private normalizeCachedResult(raw: unknown): { pois: any[]; metadata?: Record<string, any> } | null {
    if (!raw) return null

    if (Array.isArray(raw)) {
      return { pois: raw }
    }

    if (typeof raw === 'object' && raw !== null) {
      const payload = raw as { pois?: any[]; data?: any[]; metadata?: Record<string, any> }
      const pois = Array.isArray(payload.pois)
        ? payload.pois
        : Array.isArray(payload.data)
          ? payload.data
          : []
      return { pois, metadata: payload.metadata }
    }

    return null
  }

  async suggestPOIs(
    request: FastifyRequest<{
      Body: SuggestPOIsRequest
    }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        logger.warn('Suggest POIs denied: missing user context')
        return ApiResponse.unauthorized(reply)
      }

      const { location, categories, radiusKm, preferences, context, planId, stageId, planName, stageName } = request.body
      const startTime = Date.now()
      logger.info('Suggest POIs request received', {
        userId,
        categoriesCount: categories.length,
        radiusKm,
        planId,
        stageId
      });

      const cacheParams = this.buildSuggestionCacheParams(userId, location, categories, radiusKm, preferences, context, {
        planId,
        stageId,
        planName,
        stageName
      })

      // Check cache first
      const cacheKey = this.getCacheKey('suggest-pois', cacheParams)
      const cached = await this.repository.getCachedResult(cacheKey)
      const normalized = this.normalizeCachedResult(cached)

      if (normalized) {
        request.log.info('Returning cached POI suggestions')
        logger.info('POI suggestion cache hit', {
          userId,
          planId,
          stageId,
          cacheKey,
          processingTimeMs: Date.now() - startTime
        });
        const elapsed = Date.now() - startTime

        const responseMetadata = {
          ...(normalized.metadata || {}),
          source: normalized.metadata?.source || 'cache',
          cachedAt: normalized.metadata?.cachedAt || new Date().toISOString(),
          cached: true,
          processingTime: elapsed,
          processingTimeMs: elapsed
        }

        // Fire-and-forget: log cache hit (don't block response)
        void this.repository
          .saveSuggestion({
            type: 'cache-hit',
            input: { location, categories, radiusKm, preferences, context },
            output: normalized.pois,
            model: typeof normalized.metadata?.model === 'string' ? normalized.metadata.model : 'cache-hit',
            userId,
            timestamp: new Date(),
            metadata: responseMetadata
          })
          .catch(error => {
            request.log.error({ error }, 'Failed to log cache-hit suggestion')
            // Intentionally swallow error - logging cache hits should not block response
          })

        return reply.send({
          success: true,
          data: normalized.pois,
          cached: true,
          metadata: responseMetadata
        })
      }

      // Inline processing when AI worker is disabled (dev safety)
      if (!this.enableAIWorker) {
        const userTier = (request.user?.plan || 'standard') as 'free' | 'standard' | 'premium' | 'pro'
        const direct = await processPoiSuggestion({
          userId,
          userTier,
          location,
          categories,
          radiusKm,
          preferences,
          context,
          cacheKey,
          planId,
          stageId,
          planName,
          stageName
        })

        const responseMetadata = {
          ...direct.metadata,
          source: direct.metadata?.source || direct.source,
          cached: true
        }

        return reply.send({
          success: true,
          data: direct.pois,
          cached: true,
          metadata: responseMetadata
        })
      }

      // Queue job for processing
      const userTier = (request.user?.plan || 'standard') as 'free' | 'standard' | 'premium' | 'pro'
      const jobId = await this.service.queuePOISuggestion({
        userId,
        userTier,
        location,
        categories,
        radiusKm,
        preferences,
        context,
        cacheKey,
        planId,
        stageId,
        planName,
        stageName
      })

      logger.info('POI suggestion job enqueued', {
        userId,
        jobId,
        categoriesCount: categories.length,
        radiusKm,
        planId,
        stageId
      });
      void (await this.getGamificationService()).spendFuel(userId, 10, 'ai_poi_suggestion')
        .catch(error => {
          request.log.error({ error }, 'Failed to spend fuel for AI POI suggestion')
          // Don't block response – job already queued
        })
      return reply.send({ success: true, jobId })
    } catch (error) {
      logger.error('Failed to queue POI suggestion', { error, userId: request.user?.uid })
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to queue job')
    }
  }

  /**
   * Streaming POI suggestions (Server-Sent Events)
   * Returns progressive results as they become available
   */
  async suggestPOIsStream(
    request: FastifyRequest<{
      Body: SuggestPOIsRequest
    }>,
    reply: FastifyReply
  ): Promise<void> {
    const userId = request.user?.uid
    if (!userId) {
      await ApiResponse.unauthorized(reply)
      return
    }

    const { location, categories, radiusKm, preferences, context, planId, stageId, planName, stageName } = request.body

    // Set up SSE headers
    reply.raw.writeHead(200, {
      'Content-Type': 'text/event-stream',
      'Cache-Control': 'no-cache',
      'Connection': 'keep-alive'
    })

    const send = (data: any) => {
      reply.raw.write(`data: ${JSON.stringify(data)}\n\n`)
    }

    try {
      // 1. Check cache first
      const cacheParams = this.buildSuggestionCacheParams(userId, location, categories, radiusKm, preferences, context, {
        planId, stageId, planName, stageName
      })
      const cacheKey = this.getCacheKey('suggest-pois', cacheParams)
      const cached = await this.repository.getCachedResult(cacheKey)
      const normalized = this.normalizeCachedResult(cached)

      if (normalized) {
        send({ type: 'complete', pois: normalized.pois, cached: true, metadata: normalized.metadata })
        reply.raw.end()
        return
      }

      // 2. Progressive streaming from sources
      send({ type: 'progress', message: 'Recherche de suggestions...', percent: 10 })

      const userTier = (request.user?.plan || 'standard') as 'free' | 'standard' | 'premium' | 'pro'

      // Process with streaming feedback (simplified inline version)
      const result = await processPoiSuggestion({
        userId,
        userTier,
        location,
        categories,
        radiusKm,
        preferences,
        context,
        cacheKey,
        planId,
        stageId,
        planName,
        stageName
      })

      if (result.success && result.pois) {
        send({ type: 'complete', pois: result.pois, metadata: result.metadata, percent: 100 })
      } else {
        send({ type: 'error', message: 'Aucune suggestion trouvée' })
      }

      reply.raw.end()
      return
    } catch (error) {
      logger.error('Streaming POI suggestions failed', { error, userId })
      send({ type: 'error', message: 'Erreur lors de la génération' })
      reply.raw.end()
      return
    }
  }

  async enhancePlan(
    request: FastifyRequest<{
      Body: {
        planId: string
        stops?: any[]
        preferences?: any
        constraints?: any
      }
    }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        logger.warn('Enhance plan denied: missing user')
        return ApiResponse.unauthorized(reply)
      }

      const { planId, stops, preferences, constraints } = request.body
      logger.info('Enhance plan request received', {
        userId,
        planId,
        stopsCount: stops?.length ?? 0,
        constraints: constraints ? Object.keys(constraints).length : 0
      });

      // Check fuel BEFORE queueing job (15 fuel required)
      const hasFuel = await (await this.getGamificationService()).hasFuel(userId, 15)
      if (!hasFuel) {
        logger.warn('Enhance plan blocked: insufficient fuel', { userId, planId })
        return ApiResponse.badRequest(reply, 'Insufficient fuel for AI plan enhancement. Regenerates daily at midnight.')
      }

      const jobId = await this.service.queuePlanEnhancement({
        userId,
        planId,
        stops,
        preferences,
        constraints
      })

      // Spend fuel AFTER queueing job (async, don't block response)
      void (await this.getGamificationService()).spendFuel(userId, 15, 'ai_plan_enhancement')
        .catch(error => {
          request.log.error({ error }, 'Failed to spend fuel for AI plan enhancement')
          // Don't throw - job already queued
        })

      logger.info('Plan enhancement job enqueued', { userId, planId, jobId })
      return reply.send({ success: true, jobId })
    } catch (error) {
      logger.error('Failed to queue plan enhancement job', { error, userId: request.user?.uid })
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to queue job')
    }
  }

  async getJobStatus(
    request: FastifyRequest<{
      Params: { jobId: string }
    }>,
    reply: FastifyReply
  ) {
    try {
      logger.debug('Fetching AI job status', { jobId: request.params.jobId });
      const status = await this.service.getJobStatus(request.params.jobId)
      logger.info('AI job status resolved', { jobId: request.params.jobId, state: status.state })
      return reply.send(status)
    } catch (error) {
      logger.error('Failed to fetch AI job status', { error, jobId: request.params.jobId })
      request.log.error(error)
      return ApiResponse.notFound(reply, 'Job not found')
    }
  }

  /**
   * Get contextual suggestions along a route (fast, no queue)
   */
  async getContextualSuggestions(
    request: FastifyRequest<{
      Body: {
        currentLocation: { lat: number; lng: number }
        destination: { lat: number; lng: number }
        timeAvailable?: number
        query?: string
      }
    }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        logger.warn('Contextual suggestions denied: missing user')
        return ApiResponse.unauthorized(reply)
      }

      const { currentLocation, destination, timeAvailable, query } = request.body
      const startTime = Date.now()
      logger.info('Contextual suggestions requested', {
        userId,
        timeAvailable,
        query: query ?? null
      });

      // Quick database lookup for POIs along the route (no AI queue)
      const suggestions = await this.repository.getPOIsAlongRoute(
        currentLocation,
        destination,
        timeAvailable ? Math.min(timeAvailable / 2, 30) : 15,
        query
      )

      logger.info('Contextual suggestions generated', {
        userId,
        results: suggestions.length,
        durationMs: Date.now() - startTime
      });

      return reply.send({
        success: true,
        data: suggestions.slice(0, 3), // Limit to 3 for quick response
        metadata: {
          processingTime: Date.now() - startTime
        }
      })
    } catch (error) {
      logger.error('Failed to fetch contextual suggestions', { error, userId: request.user?.uid })
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to fetch suggestions')
    }
  }

  async getDashboardSuggestions(
    request: FastifyRequest<{
      Body: {
        planId: string
        context: 'pre' | 'during' | 'spark'
        anchorStageId?: string
        categories?: string[]
        radiusKm?: number
      }
    }>,
    reply: FastifyReply
  ) {
    const userId = request.user?.uid
    if (!userId) {
      logger.warn('Dashboard suggestions denied: missing user')
      return ApiResponse.unauthorized(reply)
    }

    if (!this.plannerRepository) {
      logger.error('Planner repository unavailable for dashboard suggestions')
      request.log.error('Planner repository unavailable for dashboard suggestions')
      return ApiResponse.internalError(reply, 'Planner data unavailable')
    }

    const startTime = Date.now()
    logger.info('Dashboard suggestions requested', {
      userId,
      planId: request.body.planId,
      context: request.body.context,
      anchorStageId: request.body.anchorStageId ?? null
    });

    try {
      const { planId, context, anchorStageId, categories, radiusKm } = request.body
      const plan = await this.plannerRepository.findPlanById(planId)

      if (!plan || plan.userId !== userId) {
        logger.warn('Dashboard suggestion forbidden', { userId, planId })
        return ApiResponse.forbidden(reply)
      }

      const preferences = await this.getPlannerPreferences(userId)
      const resolvedCategories = this.resolveCategories(categories, preferences)
      const resolvedRadius = this.resolveRadius(radiusKm, preferences)
      const anchorStage = this.resolveAnchorStage(plan, anchorStageId)

      if (!anchorStage) {
        logger.warn('Dashboard suggestions aborted: missing anchor stage', { planId, userId })
        return ApiResponse.badRequest(reply, 'Unable to determine anchor stage for suggestions')
      }

      const anchorCoordinates = extractCoordinates(anchorStage?.location)
      if (!anchorCoordinates) {
        logger.warn('Dashboard suggestions aborted: anchor missing coordinates', { planId, stageId: anchorStage.id })
        return ApiResponse.badRequest(reply, 'Anchor stage is missing coordinates')
      }

      let suggestions: any[] = []
      let source = 'pre-trip'

      if (context === 'during') {
        const nextStage = this.resolveNextStage(plan, anchorStage)
        if (nextStage) {
          const nextCoordinates = extractCoordinates(nextStage?.location)
          if (nextCoordinates) {
            suggestions = await this.repository.getPOIsAlongRoute(
              anchorCoordinates,
              nextCoordinates,
              Math.min(resolvedRadius, 60),
              undefined
            )
            source = 'route-context'
          }
        }

        if (!suggestions.length) {
          suggestions = await this.repository.getNearbyPOIs(anchorCoordinates, resolvedRadius)
          source = 'route-fallback'
        }
      } else {
        const effectiveCategories =
          context === 'spark' && !resolvedCategories.length
            ? await this.resolveSparkCategories(userId)
            : resolvedCategories

        suggestions = await this.repository.getNearbyPOIs(anchorCoordinates, resolvedRadius)
        if (effectiveCategories.length) {
          suggestions = suggestions.filter(poi => effectiveCategories.includes(poi.category))
        }
        source = context === 'spark' ? 'spark-trending' : 'pre-trip'
        resolvedCategories.splice(0, resolvedCategories.length, ...effectiveCategories)
      }

      const limited = suggestions.slice(0, 8)

      appEvents.emit(AppEvent.AI_SUGGESTION_USED, {
        userId,
        planId,
        stageId: anchorStage.id,
        context,
        timestamp: Date.now()
      })

      void this.repository
        .saveSuggestion({
          type: `dashboard:${context}`,
          input: {
            planId,
            stageId: anchorStage.id,
            categories: resolvedCategories,
            radiusKm: resolvedRadius,
            context
          },
          output: limited,
          model: 'dashboard-orchestrator',
          userId,
          timestamp: new Date(),
          metadata: { source }
        })
        .catch(error => {
          request.log.error(
            { error, planId, stageId: anchorStage.id, context, source },
            'Failed to log dashboard suggestion'
          )
        })

      logger.info('Dashboard suggestions generated', {
        userId,
        planId,
        context,
        resultCount: limited.length,
        durationMs: Date.now() - startTime,
        source
      });

      return reply.send({
        context,
        pois: limited,
        categories: resolvedCategories,
        radiusKm: resolvedRadius,
        metadata: {
          source,
          processingTimeMs: Date.now() - startTime,
          cached: false
        }
      })
    } catch (error) {
      logger.error('Failed to generate dashboard suggestions', {
        error,
        userId,
        planId: request.body?.planId
      })
      request.log.error(error, 'Failed to generate dashboard suggestions')
      return ApiResponse.internalError(reply, 'Failed to generate dashboard suggestions')
    }
  }

  /**
   * Get AI queue status
   */
  async getQueueStatus(
    request: FastifyRequest,
    reply: FastifyReply
  ) {
    try {
      const status = await this.service.getQueueStatus()
      return reply.send({
        success: true,
        data: status
      })
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to get queue status')
    }
  }

  async getInsights(request: FastifyRequest, reply: FastifyReply) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      const plan = resolveUserPlan(request)
      const limits = AI_QUOTA_LIMITS[plan]

      const [usageCounts, queueStatus, insights] = await Promise.all([
        this.repository.getUsageStats(userId),
        this.service.getQueueStatus(),
        this.repository.getUserInsights(userId)
      ])

      const remaining = {
        daily: Math.max(0, limits.daily - usageCounts.dailyCount),
        monthly: Math.max(0, limits.monthly - usageCounts.monthlyCount)
      }

      const now = new Date()
      const nextDailyReset = new Date(now)
      nextDailyReset.setHours(24, 0, 0, 0)
      const nextMonthlyReset = new Date(nextDailyReset)
      nextMonthlyReset.setDate(1)
      nextMonthlyReset.setMonth(nextMonthlyReset.getMonth() + 1)
      nextMonthlyReset.setHours(0, 0, 0, 0)

      return reply.send({
        success: true,
        data: {
          usage: {
            usage: usageCounts,
            limits,
            remaining,
            plan,
            nextReset: {
              daily: nextDailyReset.toISOString(),
              monthly: nextMonthlyReset.toISOString()
            }
          },
          queue: queueStatus,
          insights
        }
      })
    } catch (error) {
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to fetch AI insights')
    }
  }

  private async getPlannerPreferences(userId: string): Promise<PlannerPreferences | null> {
    if (!this.plannerRepository) {
      return null
    }

    try {
      return await this.plannerRepository.getPlannerPreferences(userId)
    } catch (error) {
      return null
    }
  }

  private resolveCategories(input?: string[], preferences?: PlannerPreferences | null): string[] {
    if (Array.isArray(input) && input.length) {
      return this.normalizeCategories(input)
    }

    if (preferences?.favoriteCategories?.length) {
      return this.normalizeCategories(preferences.favoriteCategories)
    }

    return []
  }

  private normalizeCategories(values: string[]): string[] {
    const result: string[] = []
    for (const raw of values) {
      if (!isString(raw)) continue
      const trimmed = raw.trim().toLowerCase()
      if (!trimmed) continue
      if (!result.includes(trimmed)) {
        result.push(trimmed)
      }
      if (result.length >= 12) {
        break
      }
    }
    return result
  }

  private resolveRadius(value?: number, preferences?: PlannerPreferences | null): number {
    const base = typeof value === 'number' ? value : preferences?.defaultRadiusKm ?? 50
    return Math.min(Math.max(Math.round(base), 5), 200)
  }

  private resolveAnchorStage(plan: Plan, anchorStageId?: string): Stage | undefined {
    const stages = Array.isArray(plan.stages) ? [...plan.stages] : []
    if (!stages.length) {
      return undefined
    }

    stages.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

    if (anchorStageId) {
      const match = stages.find(stage => stage.id === anchorStageId)
      if (match) {
        return match
      }
    }

    const upcoming = stages.find(stage => {
      const arrival = toDateSafe(stage.estimatedArrival)
      return arrival ? arrival.getTime() >= Date.now() : false
    })

    return upcoming ?? stages[0]
  }

  private resolveNextStage(plan: Plan, currentStage: Stage): Stage | undefined {
    const stages = Array.isArray(plan.stages) ? [...plan.stages] : []
    stages.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    const index = stages.findIndex(stage => stage.id === currentStage.id)
    if (index >= 0 && index < stages.length - 1) {
      return stages[index + 1]
    }
    return undefined
  }


  private async resolveSparkCategories(userId: string): Promise<string[]> {
    try {
      const insights = await this.repository.getUserInsights(userId)
      if (insights?.poi?.topCategories?.length) {
        return insights.poi.topCategories
          .map(entry => entry.name)
          .filter((name): name is string => isNonEmptyString(name))
          .slice(0, 6)
      }
    } catch (error) {
      // Swallow errors, fallback to defaults
    }

    return ['viewpoint', 'activity', 'restaurant']
  }

  /**
   * Get POIs within a map viewport
   * Optimized endpoint for displaying 50k+ POIs on a map
   * 
   * Query params:
   * - north, south, east, west: Bounding box coordinates (required)
   * - zoom: Map zoom level (optional, affects result limit)
   * - categories: Comma-separated category filter (optional)
   * - limit: Max results (optional, capped at 200)
   */
  async getPOIsInViewport(
    request: FastifyRequest<{
      Querystring: {
        north: string
        south: string
        east: string
        west: string
        zoom?: string
        categories?: string
        limit?: string
      }
    }>,
    reply: FastifyReply
  ) {
    try {
      const { north, south, east, west, zoom, categories, limit } = request.query

      // Validate bounds
      const bounds = {
        north: parseFloat(north),
        south: parseFloat(south),
        east: parseFloat(east),
        west: parseFloat(west)
      }

      if (
        Number.isNaN(bounds.north) ||
        Number.isNaN(bounds.south) ||
        Number.isNaN(bounds.east) ||
        Number.isNaN(bounds.west)
      ) {
        return ApiResponse.badRequest(reply, 'Invalid bounds parameters. Provide north, south, east, west as numbers.')
      }

      // Validate bounds are reasonable
      if (
        bounds.north <= bounds.south ||
        bounds.north < -90 || bounds.north > 90 ||
        bounds.south < -90 || bounds.south > 90 ||
        bounds.east < -180 || bounds.east > 180 ||
        bounds.west < -180 || bounds.west > 180
      ) {
        return ApiResponse.badRequest(reply, 'Invalid coordinate bounds.')
      }

      // Parse options
      const options = {
        zoom: zoom ? parseInt(zoom, 10) : undefined,
        categories: categories ? categories.split(',').map(c => c.trim()).filter(Boolean) : undefined,
        limit: limit ? Math.min(parseInt(limit, 10), 2000) : 1500  // Default 1500, max 2000
      }

      // Import POI database service
      const { POIDatabaseService } = await import('./poi-database.service.js')
      const { db } = await import('../../core/database/firebase.js')
      const poiDatabase = new POIDatabaseService(db)

      const result = await poiDatabase.searchInViewport(bounds, options)

      return reply.send({
        success: true,
        data: result
      })
    } catch (error) {
      logger.error('Failed to get POIs in viewport', { error })
      return ApiResponse.internalError(reply, 'Failed to fetch POIs')
    }
  }

  /**
   * Debug endpoint for troubleshooting POI viewport queries
   * Shows geohash breakdown, precision used, and grid generation
   */
  async debugViewportPOIs(
    request: FastifyRequest<{
      Querystring: {
        north: string
        south: string
        east: string
        west: string
        zoom?: string
        categories?: string
      }
    }>,
    reply: FastifyReply
  ) {
    try {
      const { north, south, east, west, zoom } = request.query

      const bounds = {
        north: parseFloat(north),
        south: parseFloat(south),
        east: parseFloat(east),
        west: parseFloat(west)
      }

      if (
        Number.isNaN(bounds.north) ||
        Number.isNaN(bounds.south) ||
        Number.isNaN(bounds.east) ||
        Number.isNaN(bounds.west)
      ) {
        return ApiResponse.badRequest(reply, 'Invalid bounds parameters')
      }

      const zoomLevel = zoom ? parseInt(zoom, 10) : 10

      const latDiff = Math.abs(bounds.north - bounds.south)
      const lngDiff = Math.abs(bounds.east - bounds.west)
      const approxRadiusKm = Math.max(latDiff, lngDiff) * 111 / 2

      // Compute precision (mirror logic from getPrecisionForViewport)
      let precision: 4 | 5 | 6 | 7
      if (approxRadiusKm <= 15) precision = 7
      else if (approxRadiusKm <= 30) precision = 6
      else if (approxRadiusKm <= 75) precision = 5
      else if (approxRadiusKm <= 200) precision = 5
      else precision = 4

      // Compute grid size (from getGeohashesForBounds)
      let gridSize: number
      if (precision <= 4) gridSize = 10
      else if (precision === 5) gridSize = 8
      else if (precision === 6) gridSize = 6
      else if (precision === 7) gridSize = 5
      else gridSize = 4

      const latRange = bounds.north - bounds.south
      const lngRange = bounds.east - bounds.west
      const latStep = latRange / gridSize
      const lngStep = lngRange / gridSize

      // Generate geohashes
      const geohashes: string[] = []
      for (let lat = bounds.south; lat <= bounds.north + 0.0001; lat += latStep) {
        for (let lng = bounds.west; lng <= bounds.east + 0.0001; lng += lngStep) {
          const h = geohash(lat, lng, precision)
          if (!geohashes.includes(h)) {
            geohashes.push(h)
          }
        }
      }

      const maxHashes = zoomLevel < 8 ? Math.min(50, geohashes.length) : Math.min(30, geohashes.length)
      const fallbackThreshold = 5

      return reply.send({
        success: true,
        debug: {
          zoom: zoomLevel,
          bounds,
          viewportSizeKm: {
            lat: Math.round(latRange * 111),
            lng: Math.round(lngRange * 111)
          },
          approxRadiusKm: Math.round(approxRadiusKm),
          precision,
          geohashField: `geohash${precision}`,
          gridSize,
          gridDimensions: {
            latStep: latStep.toFixed(4),
            lngStep: lngStep.toFixed(4)
          },
          totalGeohashesGenerated: geohashes.length,
          maxGeohashesToQuery: maxHashes,
          sampleGeohashes: geohashes.slice(0, 10),
          fallbackBehavior: {
            enabled: precision > 4,
            fallbackThreshold,
            fallbackPrecision: precision > 4 ? precision - 1 : undefined,
            message: 'If totalDocsFound < fallbackThreshold, query will retry with fallbackPrecision'
          },
          implementation: 'Chunked in-queries (10 hashes per Firestore query)',
          estimatedQueries: Math.ceil(maxHashes / 10)
        }
      })
    } catch (error) {
      logger.error('Debug viewport POIs failed', { error })
      return ApiResponse.internalError(reply, 'Debug query failed')
    }
  }

  /**
   * Get full POI details by ID
   * Called when user clicks on a POI marker (only fetches if needed)
   */
  async getPOIDetails(
    request: FastifyRequest<{
      Params: {
        poiId: string
      }
    }>,
    reply: FastifyReply
  ) {
    try {
      const { poiId } = request.params

      if (!poiId || typeof poiId !== 'string') {
        return ApiResponse.badRequest(reply, 'Invalid POI ID')
      }

      if (!this.firestore) {
        return ApiResponse.internalError(reply, 'Database not initialized')
      }

      const poiDb = new POIDatabaseService(this.firestore)
      const poi = await poiDb.getPOIDetails(poiId)

      if (!poi) {
        return ApiResponse.notFound(reply, 'POI not found')
      }

      return reply.send({
        success: true,
        data: poi
      })
    } catch (error) {
      logger.error('Failed to get POI details', { error })
      return ApiResponse.internalError(reply, 'Failed to fetch POI details')
    }
  }



  /**
   * PHASE 1: Parse text only - Returns structured segments for user review
   * User can modify segments before generating actual stages
   */
  async enrichText(
    request: FastifyRequest<{
      Body: {
        text: string
        type?: 'text' | 'voice'
        language?: string
        phase: 'parse' | 'generate'
        segments?: any[]
        preferences?: {
          discoveryMode?: number
          weatherAware?: boolean
          maxPoiPerSegment?: number
          minPoiRating?: number
        }
        userLocation?: { lat: number; lng: number }
      }
    }>,
    reply: FastifyReply
  ) {
    try {
      const { text, type = 'text', language = 'fr', phase, segments: providedSegments, preferences = {}, userLocation } = request.body
      const userId = (request as any).user?.uid

      // PHASE 1: PARSE - Just parse text into segments
      if (phase === 'parse') {
        if (!text || typeof text !== 'string' || text.trim().length === 0) {
          return ApiResponse.badRequest(reply, 'Text input is required')
        }

        logger.info('Enrichment Phase 1: Parsing text', {
          textLength: text.length,
          userId
        })

        const segments = await this.openaiService.parseActivitySegments(
          { text, type, confidence: 95, language },
          { userId, feature: 'ai:enrichment:parse' }
        )

        return reply.send({
          success: true,
          data: {
            phase: 'parse',
            segments,
            message: segments.length === 0
              ? 'Aucune étape détectée. Essayez d\'être plus précis.'
              : `${segments.length} segment(s) détecté(s). Vérifiez et modifiez si nécessaire.`
          }
        })
      }

      // PHASE 2: GENERATE - Generate actual stages with POI search
      if (phase === 'generate') {
        if (!providedSegments || !Array.isArray(providedSegments) || providedSegments.length === 0) {
          return ApiResponse.badRequest(reply, 'Segments are required for generation phase')
        }

        logger.info('Enrichment Phase 2: Generating stages', {
          segmentCount: providedSegments.length,
          userId,
          hasUserLocation: !!userLocation
        })

        const poiDb = new POIDatabaseService(this.firestore!)
        const suggestedStages: any[] = []

        for (const segment of providedSegments) {
          // Skip departure/arrival
          if (segment.type === 'departure' || segment.type === 'arrival') {
            continue
          }

          let poiCandidates: any[] = []
          const searchLocation = segment.coordinates || userLocation

          // 1. Search POIs in database if we have location
          if (searchLocation && (segment.searchQuery || segment.categories?.length > 0)) {
            try {
              poiCandidates = await poiDb.searchNearby(
                searchLocation,
                segment.categories || ['restaurant', 'hotel', 'attraction'],
                segment.searchQuery ? 10 : 5,
                {
                  minConfidence: (preferences.minPoiRating || 0) * 20, // Convert rating to confidence
                  limit: (preferences.maxPoiPerSegment || 5) + 3 // Get extra for filtering
                }
              )

              // Score and filter POIs based on relevance
              if (poiCandidates.length > 0) {
                // Calculate smart timing and scores for each POI
                const enrichedPOIs = await Promise.all(
                  poiCandidates.map(async (poi: any) => {
                    const relevance = await this.smartTimingService.calculateRelevanceScore(
                      poi,
                      segment,
                      { detourKm: (poi.distance || 0) / 1000 }
                    )

                    const timing = await this.smartTimingService.calculateOptimalTiming(
                      segment,
                      { lat: poi.latitude || poi.lat, lng: poi.longitude || poi.lng }
                    )

                    return {
                      ...poi,
                      smartScore: relevance.score,
                      scoreBreakdown: relevance.breakdown,
                      optimalTime: timing.optimalTime,
                      timingScore: timing.overallScore,
                      weatherScore: timing.weatherScore,
                      reasoning: relevance.reasoning
                    }
                  })
                )

                // Sort by smart score and filter
                poiCandidates = enrichedPOIs
                  .sort((a: any, b: any) => (b.smartScore || 0) - (a.smartScore || 0))
                  .slice(0, preferences.maxPoiPerSegment || 5)
              }
            } catch (error) {
              logger.warn('POI search failed', { segment: segment.id, error })
            }
          }

          // 2. Try geocoding if no POI found but we have a search query
          if (poiCandidates.length === 0 && segment.searchQuery && segment.fixedLocation) {
            try {
              const geocoded = await this.geocodeAddress(segment.searchQuery)
              if (geocoded) {
                logger.info('Geocoded address', { query: segment.searchQuery, result: geocoded })
                // Create POI from geocoded result
                poiCandidates = [{
                  name: geocoded.place_name,
                  formatted_address: geocoded.place_name,
                  address: geocoded.place_name,
                  latitude: geocoded.center[1],
                  lat: geocoded.center[1],
                  longitude: geocoded.center[0],
                  lng: geocoded.center[0],
                  place_id: geocoded.id,
                  placeId: geocoded.id,
                  confidence: 90,
                  source: 'mapbox_geocoding',
                  categories: segment.categories || [],
                  distance: 0
                }]
              }
            } catch (error) {
              logger.warn('Geocoding failed', { query: segment.searchQuery, error })
            }
          }

          // 3. Create stage from best POI or segment info
          if (poiCandidates.length > 0) {
            const bestPOI = poiCandidates[0]

            // Generate smart name from segment description
            const smartName = this.generateSmartStageName(segment, bestPOI)
            const category = this.detectStageCategory(segment, bestPOI)

            suggestedStages.push({
              stage: {
                id: `stage_${Date.now()}_${Math.random()}`,
                name: smartName,
                notes: segment.description,
                category,
                location: {
                  address: bestPOI.formatted_address || bestPOI.address || '',
                  coordinates: {
                    lat: bestPOI.latitude || bestPOI.lat,
                    lng: bestPOI.longitude || bestPOI.lng
                  },
                  placeId: bestPOI.place_id || bestPOI.placeId,
                  name: bestPOI.name
                },
                estimatedArrival: bestPOI.optimalTime || segment.estimatedArrival,
                estimatedDeparture: segment.estimatedDeparture,
                duration: segment.duration,
                order: suggestedStages.length
              },
              confidence: Math.round(bestPOI.smartScore || segment.confidence || 80),
              source: 'poi_database',
              reasoning: bestPOI.reasoning || `POI trouvé: ${bestPOI.name} à ${Math.round(bestPOI.distance || 0)}m`,
              poi: bestPOI,
              smartScores: {
                overall: bestPOI.smartScore,
                breakdown: bestPOI.scoreBreakdown,
                timing: bestPOI.timingScore,
                weather: bestPOI.weatherScore,
                optimalTime: bestPOI.optimalTime
              },
              alternatives: poiCandidates.slice(1, 4).map((poi: any) => ({
                stage: {
                  name: poi.name,
                  location: {
                    address: poi.formatted_address || poi.address,
                    coordinates: {
                      lat: poi.latitude || poi.lat,
                      lng: poi.longitude || poi.lng
                    }
                  }
                },
                score: poi.smartScore || poi.score || 0,
                optimalTime: poi.optimalTime,
                reasoning: poi.reasoning
              }))
            })
          } else {
            // No POI found, create stage from segment
            const smartName = this.generateSmartStageName(segment)
            const category = this.detectStageCategory(segment)

            suggestedStages.push({
              stage: {
                id: `stage_${Date.now()}_${Math.random()}`,
                name: smartName,
                notes: segment.description,
                category,
                location: segment.coordinates ? {
                  address: segment.searchQuery || 'À préciser',
                  coordinates: segment.coordinates
                } : {
                  address: segment.searchQuery || 'À préciser',
                  coordinates: { lat: 0, lng: 0 }
                },
                estimatedArrival: segment.estimatedArrival,
                estimatedDeparture: segment.estimatedDeparture,
                duration: segment.duration,
                order: suggestedStages.length
              },
              confidence: Math.max(40, segment.confidence - 20),
              source: 'user_text',
              reasoning: segment.coordinates ? 'Position définie par l\'utilisateur' : 'Géocodage nécessaire',
              needsGeocoding: !segment.coordinates && !!segment.searchQuery,
              alternatives: []
            })
          }
        }

        // Calculate total distance and duration
        let totalDistanceKm = 0
        let totalDurationMin = 0
        let earliestArrival: Date | undefined
        let latestDeparture: Date | undefined

        for (let i = 0; i < suggestedStages.length; i++) {
          const stage = suggestedStages[i]

          // Track earliest/latest times
          if (stage.stage.estimatedArrival) {
            const arrival = new Date(stage.stage.estimatedArrival)
            if (!earliestArrival || arrival < earliestArrival) {
              earliestArrival = arrival
            }
          }
          if (stage.stage.estimatedDeparture) {
            const departure = new Date(stage.stage.estimatedDeparture)
            if (!latestDeparture || departure > latestDeparture) {
              latestDeparture = departure
            }
          }

          // Add stage duration
          if (stage.stage.duration) {
            totalDurationMin += stage.stage.duration
          }

          // Calculate distance to next stage (rough estimate)
          if (i < suggestedStages.length - 1) {
            const nextStage = suggestedStages[i + 1]
            if (stage.stage.location?.coordinates && nextStage.stage.location?.coordinates) {
              const from = stage.stage.location.coordinates
              const to = nextStage.stage.location.coordinates

              // Haversine formula for rough distance
              const R = 6371 // Earth radius in km
              const dLat = (to.lat - from.lat) * Math.PI / 180
              const dLng = (to.lng - from.lng) * Math.PI / 180
              const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos(from.lat * Math.PI / 180) * Math.cos(to.lat * Math.PI / 180) *
                Math.sin(dLng / 2) * Math.sin(dLng / 2)
              const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
              const distanceKm = R * c

              totalDistanceKm += distanceKm

              // Add travel time estimate (50km/h average)
              const travelTimeMin = Math.round((distanceKm / 50) * 60)
              totalDurationMin += travelTimeMin
            }
          }
        }

        logger.info('Enrichment Phase 2 completed', {
          segmentCount: providedSegments.length,
          stageCount: suggestedStages.length,
          totalDistanceKm: Math.round(totalDistanceKm),
          totalDurationMin,
          userId
        })

        return reply.send({
          success: true,
          data: {
            phase: 'generate',
            stages: suggestedStages,
            metadata: {
              totalSegments: providedSegments.length,
              totalStages: suggestedStages.length,
              totalDistanceKm: Math.round(totalDistanceKm * 10) / 10,
              totalDurationMin,
              totalDurationHours: Math.round(totalDurationMin / 60 * 10) / 10,
              earliestArrival: earliestArrival?.toISOString(),
              latestDeparture: latestDeparture?.toISOString(),
              estimatedTripDuration: latestDeparture && earliestArrival
                ? Math.round((latestDeparture.getTime() - earliestArrival.getTime()) / (1000 * 60))
                : null,
              language,
              timestamp: new Date().toISOString()
            }
          }
        })
      }

      return ApiResponse.badRequest(reply, 'Invalid phase. Must be "parse" or "generate"')
    } catch (error) {
      logger.error('Enrichment failed', { error })
      return ApiResponse.internalError(reply, 'Enrichment failed: ' + (error instanceof Error ? error.message : 'Unknown error'))
    }
  }

  /**
   * Score and filter POIs based on relevance to segment
   * Scoring factors:
   * - Name/description match with search query
   * - Category match
   * - Distance from location
   * - Confidence score
   */
  private scoreAndFilterPOIs(
    pois: any[],
    segment: any,
    searchLocation: { lat: number; lng: number },
    limit: number
  ): any[] {
    const scoredPOIs = pois.map(poi => {
      let score = 0

      // Base score from POI confidence
      score += (poi.confidence || 50) * 0.4

      // Name/description relevance
      if (segment.searchQuery) {
        const query = segment.searchQuery.toLowerCase()
        const name = (poi.name || '').toLowerCase()
        const desc = (poi.description || '').toLowerCase()

        if (name.includes(query)) score += 30
        else if (desc.includes(query)) score += 15

        // Fuzzy matching bonus
        const queryWords = query.split(' ')
        const matchingWords = queryWords.filter((word: string) =>
        (poi.name?.toLowerCase().includes(word) ||
          poi.description?.toLowerCase().includes(word))
        ).length
        score += (matchingWords / queryWords.length) * 10
      }

      // Category match
      if (segment.categories && segment.categories.length > 0) {
        const poiCategory = this.mapPOICategoryToStageCategory(poi.category || '')
        if (segment.categories.includes(poiCategory)) {
          score += 20
        }
      }

      // Distance penalty (prefer closer POIs)
      const distance = poi.distance || 0
      if (distance < 2) score += 15
      else if (distance < 5) score += 10
      else if (distance < 10) score += 5
      else score -= Math.min(distance * 0.5, 20)

      // Time of day relevance
      if (segment.estimatedArrival) {
        const hour = new Date(segment.estimatedArrival).getHours()
        const category = this.mapPOICategoryToStageCategory(poi.category || '')

        // Restaurant scoring by time
        if (category === 'restaurant') {
          if (hour >= 12 && hour <= 14) score += 10 // Lunch time
          else if (hour >= 19 && hour <= 21) score += 10 // Dinner time
          else if (hour >= 7 && hour <= 10) score += 5 // Breakfast
        }

        // Hotel scoring by time
        if (category === 'hotel') {
          if (hour >= 18 || hour <= 8) score += 15 // Evening/morning
        }

        // Viewpoint scoring for sunset
        if (category === 'viewpoint') {
          if (hour >= 17 && hour <= 20) score += 15 // Golden hour
        }
      }

      return { ...poi, relevanceScore: score }
    })

    // Sort by score and return top results
    return scoredPOIs
      .sort((a, b) => b.relevanceScore - a.relevanceScore)
      .slice(0, limit)
  }

  /**
   * Generate smart stage name from segment + POI
   * Transforms "balade coucher du soleil" → "Balade au coucher de soleil au Belvédère X"
   */
  private generateSmartStageName(segment: any, poi?: any): string {
    let name = ''

    if (poi && poi.name) {
      // POI found - use POI name
      name = poi.name

      // Add context from segment if relevant
      if (segment.description && !segment.description.toLowerCase().includes(poi.name.toLowerCase())) {
        const activityType = this.extractActivityType(segment.description)
        if (activityType) {
          name = `${activityType} - ${poi.name}`
        }
      }
    } else if (segment.searchQuery) {
      // No POI but has search query - capitalize and improve
      name = this.capitalizeTitle(segment.searchQuery)
    } else {
      // Fallback to description - improve it
      name = this.capitalizeTitle(segment.description || 'Étape')
    }

    return name.substring(0, 100) // Max 100 chars
  }

  /**
   * Extract activity type from description
   * "balade coucher du soleil" → "Balade"
   */
  private extractActivityType(description: string): string | null {
    const activities = [
      'balade', 'visite', 'restaurant', 'pause', 'arrêt', 'détente',
      'découverte', 'exploration', 'randonnée', 'déjeuner', 'dîner', 'petit-déjeuner',
      'shopping', 'activité', 'sport', 'culture', 'monument'
    ]

    const lower = description.toLowerCase()
    for (const activity of activities) {
      if (lower.startsWith(activity)) {
        return this.capitalizeTitle(activity)
      }
    }

    return null
  }

  /**
   * Capitalize first letter of each word (smart - skip articles)
   * "balade coucher du soleil" → "Balade coucher du soleil"
   * "visite du château" → "Visite du château"
   */
  private capitalizeTitle(text: string): string {
    const skipWords = ['le', 'la', 'les', 'un', 'une', 'des', 'du', 'de', 'au', 'aux', 'à', 'et', 'ou', 'pour', 'avec', 'sans', 'dans', 'sur', 'sous']

    return text
      .split(' ')
      .map((word, idx) => {
        const lower = word.toLowerCase()
        // Always capitalize first word, or if not a skip word
        if (idx === 0 || !skipWords.includes(lower)) {
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        }
        return lower
      })
      .join(' ')
  }

  /**
   * Detect stage category from segment and POI
   */
  private detectStageCategory(segment: any, poi?: any): string {
    // Priority 1: POI category if available
    if (poi && poi.category) {
      return this.mapPOICategoryToStageCategory(poi.category)
    }

    // Priority 2: Segment categories
    if (segment.categories && segment.categories.length > 0) {
      return this.mapPOICategoryToStageCategory(segment.categories[0])
    }

    // Priority 3: Detect from description keywords
    const description = (segment.description || segment.searchQuery || '').toLowerCase()

    const categoryKeywords: Record<string, string[]> = {
      'restaurant': ['restaurant', 'café', 'manger', 'food', 'eat', 'dining', 'bistro', 'déjeuner', 'dîner'],
      'hotel': ['hotel', 'gite', 'camping', 'dormir', 'sleep', 'nuit', 'overnight', 'auberge'],
      'culture': ['musée', 'museum', 'monument', 'theatre', 'culture', 'art', 'château', 'castle'],
      'nature': ['parc', 'park', 'nature', 'randonnée', 'hiking', 'forêt', 'balade'],
      'viewpoint': ['vue', 'view', 'panorama', 'belvédère', 'lookout', 'photo', 'sunset', 'coucher soleil'],
      'activity': ['sport', 'activité', 'activity', 'leisure', 'adventure', 'fun'],
      'shopping': ['shopping', 'boutique', 'shop', 'marché', 'market', 'courses'],
      'fuel': ['essence', 'fuel', 'gas', 'station', 'carburant'],
      'parking': ['parking', 'garer', 'aire'],
      'health': ['pharmacie', 'pharmacy', 'hopital', 'hospital', 'docteur', 'doctor'],
      'grocery': ['courses', 'grocery', 'supermarché', 'supermarket', 'intermarché', 'carrefour'],
      'beach': ['plage', 'beach', 'mer', 'sea', 'baignade'],
    }

    for (const [category, keywords] of Object.entries(categoryKeywords)) {
      if (keywords.some(kw => description.includes(kw))) {
        return category
      }
    }

    return 'other'
  }

  /**
   * Map POI database categories to our unified categories
   */
  private mapPOICategoryToStageCategory(poiCategory: string): string {
    const mapping: Record<string, string> = {
      'food': 'restaurant',
      'restaurant': 'restaurant',
      'cafe': 'restaurant',
      'bar': 'restaurant',
      'accommodation': 'hotel',
      'hotel': 'hotel',
      'motel': 'hotel',
      'camping': 'hotel',
      'tourism': 'culture',
      'museum': 'culture',
      'monument': 'culture',
      'attraction': 'viewpoint',
      'viewpoint': 'viewpoint',
      'park': 'nature',
      'nature': 'nature',
      'sport': 'activity',
      'leisure': 'activity',
      'shop': 'shopping',
      'supermarket': 'grocery',
      'fuel': 'fuel',
      'parking': 'parking',
      'health': 'health',
      'pharmacy': 'health',
      'hospital': 'health',
    }

    const lower = poiCategory.toLowerCase()
    return mapping[lower] || 'other'
  }

  /**
   * Geocode address using Mapbox Geocoding API
   */
  private async geocodeAddress(query: string): Promise<any | null> {
    const mapboxToken = process.env.MAPBOX_ACCESS_TOKEN
    if (!mapboxToken) {
      logger.warn('Mapbox token not configured, skipping geocoding')
      return null
    }

    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${mapboxToken}&limit=1`
      const response = await fetch(url)

      if (!response.ok) {
        throw new Error(`Mapbox API error: ${response.status}`)
      }

      const data = await response.json() as { features?: any[] }

      if (data.features && data.features.length > 0) {
        return data.features[0]
      }

      return null
    } catch (error) {
      logger.error('Geocoding failed', { query, error })
      return null
    }
  }
}
